/**
 * 到達性（presence）の応答契約 — Single Source of Truth
 *
 * デスクトップアプリからの定期的な KeepAlive を根拠に、各利用者が
 * **いま到達可能か**を表す。バックエンド・管理コンソールの双方がこの
 * スキーマだけを参照し、状態の呼び方が実装ごとに割れないようにする。
 *
 * **用語は `presence` /「オンライン」「オフライン」に固定する。**
 * 「接続中」「アクティブ」「稼働中」などの別称を新たに持ち込まない。
 * とりわけ管理コンソールの既存指標「アクティブユーザー」（当月に言語モデルの
 * 利用実績があった人数）とは無関係であり、名称を共有してはならない。
 */
import { z } from 'zod';
/**
 * 到達性の状態値。
 *
 * **真偽値ではなく列挙値として運ぶ。** 将来ビジー状態（タスク実行中・承認待ち）を
 * 区別したくなったとき、真偽値だと応答の形そのものを変えることになり、
 * 利用側の分岐をすべて書き換える羽目になる。
 */
export const PresenceStatusSchema = z.enum(['online', 'offline']);
/**
 * 到達性の記録がある利用者 1 人分のエントリ。
 */
export const AdminPresenceEntrySchema = z.object({
    /** 利用者の識別子。Cognito の `email` 属性。 */
    email: z.string(),
    status: PresenceStatusSchema,
    /**
     * 最終確認時刻（Unix epoch ミリ秒）。
     *
     * **アプリの起動時刻ではない。** 最後に KeepAlive を受け取った時刻であり、
     * 起動してからの経過時間や稼働時間を表すものではない。
     */
    lastSeenAt: z.number(),
});
/**
 * GET /admin/presence レスポンス。
 *
 * **応答には到達性の記録がある利用者のみが含まれる。
 * 含まれない利用者はオフラインと解釈してよい。**
 * 全利用者を列挙するには Cognito のユーザー一覧が別途必要であり、
 * 自動更新のたびに取得すると利用者数に比例した負荷になるため含めない。
 *
 * `entries.length` はそのまま「到達性の記録がある利用者数」を意味する。
 * 0 件は「誰も起動していない」ではなく「KeepAlive が届いていない可能性」を
 * 示す合図であり、記録ありでオンライン 0 人とは区別して扱うこと。
 */
export const GetAdminPresenceResponseSchema = z.object({
    entries: z.array(AdminPresenceEntrySchema),
    /**
     * 判定に用いた鮮度しきい値（秒）。
     *
     * 利用側がサーバーの判定を追認するのではなく、`lastSeenAt` と合わせて
     * 独自の厳しさで判定をやり直せるようにするために同梱する。
     */
    thresholdSeconds: z.number(),
    /** 応答生成時刻（Unix epoch ミリ秒）。利用側が経過時間を測る起点になる。 */
    generatedAt: z.number(),
});
//# sourceMappingURL=presence-schemas.js.map